<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import CustomSlaPoliciesAPI from 'dashboard/api/customSlaPolicies';
import Select from 'dashboard/components-next/select/Select.vue';

const policies = ref([]);
const selectedPolicyId = ref(null);
const selectedAgentId = ref('');
const isLoading = ref(false);
const isLoadingReport = ref(false);
const report = ref(null);
const days = ref(30);

const agents = useMapGetter('agents/getAgents');

const policyOptions = computed(() =>
  policies.value.map(p => ({ value: p.id, label: p.name }))
);

const agentOptions = computed(() => {
  const opts = [{ value: '', label: 'Todos os agentes' }];
  (agents.value || []).forEach(a => {
    opts.push({ value: a.id, label: a.available_name || a.name });
  });
  return opts;
});

const dayOptions = [
  { value: 7, label: 'Últimos 7 dias' },
  { value: 15, label: 'Últimos 15 dias' },
  { value: 30, label: 'Últimos 30 dias' },
  { value: 90, label: 'Últimos 90 dias' },
];

function formatTime(seconds) {
  if (!seconds) return '—';
  if (seconds >= 3600) return `${Math.round(seconds / 3600 * 10) / 10}h`;
  if (seconds >= 60) return `${Math.round(seconds / 60 * 10) / 10}min`;
  return `${seconds}s`;
}

function metPercent(met, total) {
  if (!total) return 0;
  return Math.round((met / total) * 100);
}

async function loadPolicies() {
  isLoading.value = true;
  try {
    const res = await CustomSlaPoliciesAPI.get();
    policies.value = res.data || [];
    if (policies.value.length > 0 && !selectedPolicyId.value) {
      selectedPolicyId.value = policies.value[0].id;
    }
  } catch { policies.value = []; }
  isLoading.value = false;
}

async function loadReport() {
  if (!selectedPolicyId.value) return;
  isLoadingReport.value = true;
  try {
    const agentId = selectedAgentId.value || null;
    const res = await CustomSlaPoliciesAPI.getReport(selectedPolicyId.value, days.value, agentId);
    report.value = res.data;
  } catch { report.value = null; }
  isLoadingReport.value = false;
}

function eventTypeLabel(type) {
  const labels = {
    first_response: '1ª Resposta',
    next_response_time: 'Próx. Resposta',
    criterion: 'Critério',
  };
  return labels[type] || type;
}

function filterByAgent(agentId) {
  selectedAgentId.value = agentId;
}

watch([selectedPolicyId, days, selectedAgentId], () => loadReport());

onMounted(async () => {
  await loadPolicies();
  if (selectedPolicyId.value) loadReport();
});
</script>

<template>
  <div class="flex flex-col flex-1 overflow-auto p-6">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">Relatório SLA</h1>
        <p class="text-sm text-n-slate-11 mt-1">Acompanhe o desempenho das políticas de SLA de atendimento.</p>
      </div>
    </div>

    <div class="flex items-end gap-4 mb-6">
      <div>
        <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Política SLA</label>
        <Select v-model="selectedPolicyId" :options="policyOptions" placeholder="Selecione..." />
      </div>
      <div>
        <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Período</label>
        <Select v-model="days" :options="dayOptions" />
      </div>
      <div>
        <label class="mb-1.5 text-heading-3 text-n-slate-12 block">Agente</label>
        <Select v-model="selectedAgentId" :options="agentOptions" />
      </div>
    </div>

    <div v-if="isLoading || isLoadingReport" class="flex items-center justify-center py-20">
      <span class="i-lucide-loader-2 animate-spin size-7 text-n-brand" />
    </div>

    <div v-else-if="policies.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
      <span class="i-lucide-timer size-16 text-n-slate-8 mb-4" />
      <h3 class="text-lg font-semibold text-n-slate-12 mb-1">Nenhuma política configurada</h3>
      <p class="text-sm text-n-slate-11 max-w-sm">Configure políticas de SLA em Configurações → SLA Atendimento.</p>
    </div>

    <template v-else-if="report">
      <div class="flex flex-wrap gap-1.5 mb-5">
        <span v-if="report.policy.only_during_business_hours" class="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <span class="i-lucide-building-2 size-3" />
          Horário comercial
        </span>
        <span v-for="inbox in (report.policy.inboxes || [])" :key="'i-' + inbox.id" class="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
          <span class="i-lucide-inbox size-3" /> {{ inbox.name }}
        </span>
        <span v-for="team in (report.policy.teams || [])" :key="'t-' + team.id" class="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
          <span class="i-lucide-users size-3" /> {{ team.name }}
        </span>
      </div>

      <div class="grid grid-cols-4 gap-4 mb-6">
        <div class="p-4 rounded-xl border border-n-weak bg-n-alpha-black2">
          <div class="flex items-center gap-2 mb-3">
            <div class="size-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <span class="i-lucide-message-circle size-4 text-blue-600 dark:text-blue-400" />
            </div>
            <span class="text-xs font-medium text-n-slate-11">1ª Resposta</span>
          </div>
          <div class="flex items-end justify-between">
            <div>
              <div class="text-2xl font-bold text-n-slate-12">{{ formatTime(report.policy.first_response_time_seconds) }}</div>
              <div v-if="report.policy.first_response_trigger_text" class="text-[10px] text-n-slate-10 mt-0.5 truncate">Gatilho: "{{ report.policy.first_response_trigger_text }}"</div>
            </div>
            <div v-if="report.summary.first_response.total > 0" class="text-right">
              <div class="text-lg font-bold" :class="metPercent(report.summary.first_response.met, report.summary.first_response.total) >= 80 ? 'text-emerald-600' : 'text-red-500'">
                {{ metPercent(report.summary.first_response.met, report.summary.first_response.total) }}%
              </div>
              <div class="text-[10px] text-n-slate-10">{{ report.summary.first_response.met }}/{{ report.summary.first_response.total }}</div>
            </div>
          </div>
          <div v-if="report.summary.first_response.total > 0" class="mt-3">
            <div class="w-full bg-n-slate-4 rounded-full h-1.5">
              <div class="h-1.5 rounded-full transition-all duration-500" :class="metPercent(report.summary.first_response.met, report.summary.first_response.total) >= 80 ? 'bg-emerald-500' : 'bg-red-500'" :style="{ width: metPercent(report.summary.first_response.met, report.summary.first_response.total) + '%' }" />
            </div>
            <div class="text-[10px] text-n-slate-10 mt-1">Média: {{ formatTime(report.summary.first_response.avg_seconds) }}</div>
          </div>
          <div v-else class="mt-3 text-[10px] text-n-slate-10">Sem dados no período</div>
        </div>

        <div class="p-4 rounded-xl border border-n-weak bg-n-alpha-black2">
          <div class="flex items-center gap-2 mb-3">
            <div class="size-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <span class="i-lucide-clock size-4 text-orange-600 dark:text-orange-400" />
            </div>
            <span class="text-xs font-medium text-n-slate-11">Próx. Resposta</span>
          </div>
          <div class="flex items-end justify-between">
            <div>
              <div class="text-2xl font-bold text-n-slate-12">{{ formatTime(report.policy.next_response_time_seconds) }}</div>
            </div>
            <div v-if="report.summary.next_response.total > 0" class="text-right">
              <div class="text-lg font-bold" :class="metPercent(report.summary.next_response.met, report.summary.next_response.total) >= 80 ? 'text-emerald-600' : 'text-red-500'">
                {{ metPercent(report.summary.next_response.met, report.summary.next_response.total) }}%
              </div>
              <div class="text-[10px] text-n-slate-10">{{ report.summary.next_response.met }}/{{ report.summary.next_response.total }}</div>
            </div>
          </div>
          <div v-if="report.summary.next_response.total > 0" class="mt-3">
            <div class="w-full bg-n-slate-4 rounded-full h-1.5">
              <div class="h-1.5 rounded-full transition-all duration-500" :class="metPercent(report.summary.next_response.met, report.summary.next_response.total) >= 80 ? 'bg-emerald-500' : 'bg-red-500'" :style="{ width: metPercent(report.summary.next_response.met, report.summary.next_response.total) + '%' }" />
            </div>
            <div class="text-[10px] text-n-slate-10 mt-1">Média: {{ formatTime(report.summary.next_response.avg_seconds) }}</div>
          </div>
          <div v-else class="mt-3 text-[10px] text-n-slate-10">Sem dados no período</div>
        </div>

        <div class="p-4 rounded-xl border border-n-weak bg-n-alpha-black2">
          <div class="flex items-center gap-2 mb-3">
            <div class="size-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <span class="i-lucide-check-circle size-4 text-green-600 dark:text-green-400" />
            </div>
            <span class="text-xs font-medium text-n-slate-11">Resolução</span>
          </div>
          <div class="flex items-end justify-between">
            <div>
              <div class="text-2xl font-bold text-n-slate-12">{{ formatTime(report.policy.resolution_time_seconds) }}</div>
            </div>
            <div v-if="report.summary.resolution && report.summary.resolution.total > 0" class="text-right">
              <div class="text-lg font-bold" :class="metPercent(report.summary.resolution.met, report.summary.resolution.total) >= 80 ? 'text-emerald-600' : 'text-red-500'">
                {{ metPercent(report.summary.resolution.met, report.summary.resolution.total) }}%
              </div>
              <div class="text-[10px] text-n-slate-10">{{ report.summary.resolution.met }}/{{ report.summary.resolution.total }}</div>
            </div>
          </div>
          <div v-if="report.summary.resolution && report.summary.resolution.total > 0" class="mt-3">
            <div class="w-full bg-n-slate-4 rounded-full h-1.5">
              <div class="h-1.5 rounded-full transition-all duration-500" :class="metPercent(report.summary.resolution.met, report.summary.resolution.total) >= 80 ? 'bg-emerald-500' : 'bg-red-500'" :style="{ width: metPercent(report.summary.resolution.met, report.summary.resolution.total) + '%' }" />
            </div>
            <div class="text-[10px] text-n-slate-10 mt-1">Média: {{ formatTime(report.summary.resolution.avg_seconds) }}</div>
          </div>
          <div v-else class="mt-3 text-[10px] text-n-slate-10">Sem dados no período</div>
        </div>

        <div class="p-4 rounded-xl border border-n-weak bg-n-alpha-black2">
          <div class="flex items-center gap-2 mb-3">
            <div class="size-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <span class="i-lucide-target size-4 text-violet-600 dark:text-violet-400" />
            </div>
            <span class="text-xs font-medium text-n-slate-11">Critérios</span>
          </div>
          <div class="text-2xl font-bold text-n-slate-12">{{ (report.criteria || []).length }}</div>
          <div class="text-[10px] text-n-slate-10 mt-1">critérios configurados</div>
        </div>
      </div>

      <div v-if="report.agents_ranking && report.agents_ranking.length > 0 && !selectedAgentId" class="mb-6">
        <div class="flex items-center gap-2 text-xs font-semibold text-n-slate-11 uppercase tracking-wider mb-3">
          <span class="i-lucide-users size-3.5" />
          Desempenho por Agente
        </div>
        <div class="grid gap-2">
          <div
            v-for="(agent, idx) in report.agents_ranking"
            :key="agent.id"
            class="flex items-center gap-4 p-3 rounded-xl border border-n-weak bg-n-alpha-black2 hover:bg-n-alpha-2 transition-colors cursor-pointer"
            @click="filterByAgent(agent.id)"
          >
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <div class="size-8 rounded-full bg-n-brand/10 flex items-center justify-center flex-shrink-0 text-sm font-bold text-n-brand">
                {{ idx + 1 }}
              </div>
              <div class="min-w-0">
                <div class="text-sm font-medium text-n-slate-12 truncate">{{ agent.name }}</div>
                <div class="text-[10px] text-n-slate-10">{{ agent.total }} eventos</div>
              </div>
            </div>
            <div class="flex items-center gap-4 flex-shrink-0">
              <div class="text-center">
                <div class="text-sm font-bold text-emerald-600">{{ agent.met }}</div>
                <div class="text-[9px] text-n-slate-10 uppercase">Dentro</div>
              </div>
              <div class="text-center">
                <div class="text-sm font-bold text-red-500">{{ agent.breached }}</div>
                <div class="text-[9px] text-n-slate-10 uppercase">Fora</div>
              </div>
              <div class="text-center">
                <div class="text-sm font-bold text-n-slate-12">{{ formatTime(agent.avg_seconds) }}</div>
                <div class="text-[9px] text-n-slate-10 uppercase">Média</div>
              </div>
              <div class="w-16">
                <div class="w-full bg-n-slate-4 rounded-full h-1.5">
                  <div
                    class="h-1.5 rounded-full transition-all duration-500"
                    :class="metPercent(agent.met, agent.total) >= 80 ? 'bg-emerald-500' : 'bg-red-500'"
                    :style="{ width: metPercent(agent.met, agent.total) + '%' }"
                  />
                </div>
                <div class="text-[9px] text-center mt-0.5" :class="metPercent(agent.met, agent.total) >= 80 ? 'text-emerald-600' : 'text-red-500'">
                  {{ metPercent(agent.met, agent.total) }}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="report.criteria && report.criteria.length > 0" class="mb-6">
        <div class="flex items-center gap-2 text-xs font-semibold text-n-slate-11 uppercase tracking-wider mb-3">
          <span class="i-lucide-target size-3.5" />
          Desempenho por Critério
        </div>
        <div class="grid gap-2">
          <div v-for="c in report.criteria" :key="c.id" class="p-3 rounded-xl border border-n-weak bg-n-alpha-black2">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <span class="i-lucide-target size-3.5 text-violet-500" />
                <span class="text-sm font-medium text-n-slate-12">{{ c.name }}</span>
                <span class="text-xs text-n-slate-10">"{{ c.trigger_text }}"</span>
              </div>
              <span class="text-sm font-semibold text-n-slate-12">{{ formatTime(c.threshold_seconds) }}</span>
            </div>
            <div v-if="c.total > 0" class="flex items-center gap-4">
              <div class="flex-1">
                <div class="w-full bg-n-slate-4 rounded-full h-1.5">
                  <div class="h-1.5 rounded-full transition-all duration-500" :class="metPercent(c.met, c.total) >= 80 ? 'bg-emerald-500' : 'bg-red-500'" :style="{ width: metPercent(c.met, c.total) + '%' }" />
                </div>
              </div>
              <div class="flex items-center gap-3 text-xs flex-shrink-0">
                <span class="text-emerald-600 font-medium">{{ c.met }} ✓</span>
                <span class="text-red-500 font-medium">{{ c.breached }} ✗</span>
                <span class="text-n-slate-10">Média: {{ formatTime(c.avg_seconds) }}</span>
              </div>
            </div>
            <div v-else class="text-[10px] text-n-slate-10">Sem dados no período</div>
          </div>
        </div>
      </div>

      <div v-if="report.recent_events && report.recent_events.length > 0">
        <div class="flex items-center gap-2 text-xs font-semibold text-n-slate-11 uppercase tracking-wider mb-3">
          <span class="i-lucide-list size-3.5" />
          Eventos Recentes
        </div>
        <div class="rounded-xl border border-n-weak overflow-hidden">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-n-alpha-black2">
                <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Tipo</th>
                <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Agente</th>
                <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Conversa</th>
                <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Limite</th>
                <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Real</th>
                <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Status</th>
                <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11 uppercase tracking-wider">Data</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="e in report.recent_events" :key="e.id" class="border-t border-n-weak hover:bg-n-alpha-1 transition-colors">
                <td class="px-4 py-2.5 text-n-slate-12">
                  <span class="inline-flex items-center gap-1">
                    <span v-if="e.event_type === 'criterion'" class="i-lucide-target size-3 text-violet-500" />
                    <span v-else-if="e.event_type === 'first_response'" class="i-lucide-message-circle size-3 text-blue-500" />
                    <span v-else class="i-lucide-clock size-3 text-orange-500" />
                    {{ e.criterion_name || eventTypeLabel(e.event_type) }}
                  </span>
                </td>
                <td class="px-4 py-2.5 text-n-slate-12">{{ e.agent_name || '—' }}</td>
                <td class="px-4 py-2.5 text-n-slate-12 font-medium">#{{ e.conversation_id }}</td>
                <td class="px-4 py-2.5 text-n-slate-11">{{ formatTime(e.threshold_seconds) }}</td>
                <td class="px-4 py-2.5 text-n-slate-12 font-medium">{{ formatTime(e.actual_seconds) }}</td>
                <td class="px-4 py-2.5">
                  <span class="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full" :class="e.met ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'">
                    <span :class="e.met ? 'i-lucide-check size-3' : 'i-lucide-x size-3'" />
                    {{ e.met ? 'Dentro' : 'Fora' }}
                  </span>
                </td>
                <td class="px-4 py-2.5 text-n-slate-10 text-xs">{{ new Date(e.created_at).toLocaleDateString('pt-BR') }} {{ new Date(e.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else-if="!isLoadingReport" class="rounded-xl border border-dashed border-n-weak p-8 text-center bg-n-alpha-black2">
        <span class="i-lucide-bar-chart-3 size-12 text-n-slate-8 mx-auto mb-3 block" />
        <h3 class="text-sm font-semibold text-n-slate-12 mb-1">Nenhum evento registrado</h3>
        <p class="text-xs text-n-slate-10 max-w-md mx-auto">Os eventos serão registrados automaticamente quando mensagens forem enviadas em conversas que correspondem a esta política.</p>
      </div>
    </template>
  </div>
</template>
